import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Guid } from 'guid-typescript';
import { Observable } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AppService } from '../app.service';
import { AccessTokenService } from './access-token.service';

export enum TeamReportStatus {
  authError = 'auth_error',
  available = 'available',
  incomplete = 'incomplete',
  isPending = 'is_pending',
  unknown = 'unknown',
}

export class TeamReportData {
  status: TeamReportStatus;
  file: File;

  constructor(status: TeamReportStatus);
  constructor(file: File);
  constructor(statusOrFile: TeamReportStatus | File) {
    if (statusOrFile instanceof File) {
      this.file = statusOrFile as File;
      this.status = TeamReportStatus.available;
    } else {
      this.file = null;
      this.status = statusOrFile as TeamReportStatus;
    }
  }
}

@Injectable({
  providedIn: 'root',
})
export class TeamReportService {
  constructor(
    private readonly appService: AppService,
    private readonly http: HttpClient,
    private readonly accessTokenService: AccessTokenService,
  ) {}

  private getStatusUrl(teamId: Guid): Observable<string> {
    return this.getWebApiUrl(`/api/mbtio/reports/team/${teamId}/member`);
  }

  private getWebApiUrl(path?: string): Observable<string> {
    return this.appService.getUserDetails().pipe(map((details) => `${details.webApiSiteBaseUrl}${path || ''}`));
  }

  getTeamReport(teamId: Guid): Observable<TeamReportData> {
    return this.accessTokenService.getAccessToken().pipe(
      switchMap((token) =>
        this.getStatusUrl(teamId).pipe(
          switchMap((url) =>
            this.http
              .get(url, {
                headers: new HttpHeaders({ Authorization: `Bearer ${token}` }),
                observe: 'response',
                responseType: 'blob',
              })
              .pipe(
                map((response) => {
                  let result: TeamReportData;
                  switch (response.status) {
                    case 200: {
                      const disposition = response.headers.get('Content-Disposition');
                      let filename = 'report.pdf';
                      if (disposition && disposition.indexOf('attachment') !== -1) {
                        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
                        if (matches && matches[1]) {
                          filename = matches[1].replace(/['"]/g, '');
                        }
                      }
                      result = new TeamReportData(new File([response.body], decodeURI(filename)));
                      break;
                    }
                    case 202:
                      result = new TeamReportData(TeamReportStatus.isPending);
                      break;
                    case 401:
                    case 403:
                      result = new TeamReportData(TeamReportStatus.authError);
                      break;
                    case 204:
                      result = new TeamReportData(TeamReportStatus.incomplete);
                      break;
                    default:
                      result = new TeamReportData(TeamReportStatus.unknown);
                      break;
                  }
                  return result;
                }),
              ),
          ),
          catchError((err) => {
            this.appService.handleError(err);
            return new Observable<TeamReportData>((ob) => ob.next(new TeamReportData(TeamReportStatus.unknown)));
          }),
        ),
      ),
      catchError((err) => {
        this.appService.handleError(err);
        return new Observable<TeamReportData>((ob) => ob.next(new TeamReportData(TeamReportStatus.authError)));
      }),
    );
  }

  getTeamReportStatus(teamId: Guid): Observable<TeamReportStatus> {
    return this.accessTokenService.getAccessToken().pipe(
      switchMap((token) =>
        this.getStatusUrl(teamId).pipe(
          switchMap((url) =>
            this.http
              .head(url, {
                headers: new HttpHeaders({ Authorization: `Bearer ${token}` }),
                observe: 'response',
              })
              .pipe(
                map((response) => {
                  let status: TeamReportStatus;
                  switch (response.status) {
                    case 200:
                      status = TeamReportStatus.available;
                      break;
                    case 202:
                      status = TeamReportStatus.isPending;
                      break;
                    case 401:
                    case 403:
                      status = TeamReportStatus.authError;
                      break;
                    case 204:
                      status = TeamReportStatus.incomplete;
                      break;
                    default:
                      status = TeamReportStatus.unknown;
                      break;
                  }
                  return status;
                }),
              ),
          ),
          catchError((err) => {
            this.appService.handleError(err);
            return new Observable<TeamReportStatus>((ob) => ob.next(TeamReportStatus.unknown));
          }),
        ),
      ),
      catchError((err) => {
        this.appService.handleError(err);
        return new Observable<TeamReportStatus>((o) => o.next(TeamReportStatus.authError));
      }),
    );
  }
}
